import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Edit, Trash2, AlertTriangle, CheckCircle, TrendingUp, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

const EMPTY = { name: "", kind: "cashback", credits: "", bonus: "", price: "", description: "", highlight: false, active: true, order: 100 };

export default function AdminPackages() {
  const [packages, setPackages] = useState([]);
  const [report, setReport] = useState(null);
  const [editing, setEditing] = useState(null); // 'NEW' or package id
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const [r1, r2] = await Promise.all([
        api.get("/admin/packages"),
        api.get("/admin/packages/margin-report"),
      ]);
      setPackages(r1.data);
      setReport(r2.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { refresh(); }, []);

  const openEdit = (p) => { setEditing(p.id); setForm({ ...p }); };
  const openNew = () => { setEditing("NEW"); setForm(EMPTY); };
  const close = () => { setEditing(null); setForm(EMPTY); };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        credits: parseFloat(form.credits) || 0,
        bonus: parseFloat(form.bonus) || 0,
        price: parseFloat(form.price) || 0,
        order: parseInt(form.order, 10) || 100,
      };
      if (editing === "NEW") await api.post("/admin/packages", payload);
      else await api.patch(`/admin/packages/${editing}`, payload);
      toast.success("Pacote salvo");
      close(); refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir pacote? (preferir desativar)")) return;
    try { await api.delete(`/admin/packages/${id}`); toast.success("Excluído"); refresh(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  // Live margin preview during edit
  const credits = parseFloat(form.credits) || 0;
  const bonus = parseFloat(form.bonus) || 0;
  const price = parseFloat(form.price) || 0;
  const totalCredits = credits + bonus;
  let margin0, margin30;
  if (form.kind === "voucher") {
    margin0 = margin30 = (price * 0.97); // net of ~3% MP
  } else {
    const netIn = price * 0.97;
    margin0 = netIn - totalCredits * 0.95;
    margin30 = netIn - totalCredits * 0.7 * 0.95;
  }
  const warn = margin0 < 0;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      <Link to="/admin" className="inline-flex items-center text-sm text-blue-900 mb-4">
        <ArrowLeft size={14} className="mr-1"/> Voltar
      </Link>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-stone-900">Lojinha de Pacotes</h1>
          <p className="text-stone-500 text-sm mt-1">Gerencie pacotes de Cashback e Voucher vendidos aos comerciantes.</p>
        </div>
        <Button onClick={openNew} className="bg-blue-900 hover:bg-blue-950 text-white rounded-xl" data-testid="new-pkg-btn">
          <Plus size={14} className="mr-1.5"/> Novo pacote
        </Button>
      </div>

      {report && (
        <Card className="p-4 rounded-2xl border-emerald-200 bg-emerald-50 mb-6">
          <h2 className="font-display text-lg font-bold text-emerald-900 mb-2 flex items-center gap-2">
            <TrendingUp size={18}/> P&L Real da Lojinha
          </h2>
          <div className="grid sm:grid-cols-4 gap-3 text-sm">
            <div><div className="text-[10px] uppercase text-emerald-700 font-bold">Vendas confirmadas</div><div className="font-display text-xl font-bold text-emerald-900">{report.total_purchases}</div></div>
            <div><div className="text-[10px] uppercase text-emerald-700 font-bold">Receita total</div><div className="font-display text-xl font-bold text-emerald-900">R$ {report.total_revenue.toFixed(2)}</div></div>
            <div><div className="text-[10px] uppercase text-emerald-700 font-bold">Créditos B$ emitidos</div><div className="font-display text-xl font-bold text-emerald-900">B$ {report.total_credits_issued.toFixed(2)}</div></div>
            <div><div className="text-[10px] uppercase text-emerald-700 font-bold">Margem Bruta (pior caso)</div><div className={`font-display text-xl font-bold ${report.raw_margin_0_breakage < 0 ? 'text-red-700' : 'text-emerald-900'}`}>R$ {report.raw_margin_0_breakage.toFixed(2)}</div></div>
          </div>
        </Card>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {packages.map((p) => {
          const m = p.margin_analysis || {};
          return (
            <Card key={p.id} className={`p-4 rounded-2xl border-2 ${m.warn_loss ? 'border-red-300 bg-red-50' : p.active ? 'border-stone-200' : 'border-stone-100 opacity-60'}`} data-testid={`pkg-${p.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${p.kind === 'cashback' ? 'bg-emerald-200 text-emerald-900' : 'bg-blue-200 text-blue-900'}`}>{p.kind}</span>
                  {p.highlight && <span className="ml-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-amber-200 text-amber-900">Destaque</span>}
                  {!p.active && <span className="ml-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-stone-300 text-stone-700">Inativo</span>}
                  <h3 className="font-display text-lg font-bold text-stone-900 mt-1">{p.name}</h3>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => openEdit(p)} className="text-stone-500 hover:text-stone-900" data-testid={`edit-pkg-${p.id}`}><Edit size={14}/></button>
                  <button onClick={() => remove(p.id)} className="text-red-500 hover:text-red-700"><Trash2 size={14}/></button>
                </div>
              </div>
              <p className="text-xs text-stone-600 mb-3">{p.description}</p>
              <div className="grid grid-cols-3 gap-2 text-center bg-stone-50 rounded-lg p-3">
                <div><div className="text-[10px] uppercase text-stone-500">Preço</div><div className="font-display font-bold text-stone-900">R$ {p.price.toFixed(2)}</div></div>
                <div><div className="text-[10px] uppercase text-stone-500">B$ líquido</div><div className="font-display font-bold text-blue-900">{(p.credits + p.bonus).toFixed(0)}</div></div>
                <div><div className="text-[10px] uppercase text-stone-500">Bônus</div><div className="font-display font-bold text-emerald-700">{p.bonus.toFixed(0)}</div></div>
              </div>
              <div className={`mt-3 p-2.5 rounded-lg text-xs flex items-center gap-2 ${m.warn_loss ? 'bg-red-100 text-red-900' : 'bg-emerald-50 text-emerald-900'}`}>
                {m.warn_loss ? <AlertTriangle size={14}/> : <CheckCircle size={14}/>}
                <div>
                  <strong>Margem estimada:</strong> 0% breakage: R$ {(m.expected_margin_0_breakage ?? 0).toFixed(2)} · 30% breakage: R$ {(m.expected_margin_30_breakage ?? 0).toFixed(2)}
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && close()}>
        <DialogContent className="max-w-2xl rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="pkg-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editing === "NEW" ? "Novo Pacote" : "Editar Pacote"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>Nome</Label><Input required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="rounded-xl mt-1" data-testid="pkg-name"/></div>
              <div>
                <Label>Tipo</Label>
                <Select value={form.kind} onValueChange={(v) => setForm({...form, kind: v})}>
                  <SelectTrigger className="rounded-xl mt-1" data-testid="pkg-kind"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cashback">Cashback (vale na rede)</SelectItem>
                    <SelectItem value="voucher">Voucher (vale só na loja)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Preço (R$ — o que o comerciante paga)</Label><Input type="number" step="0.01" min="0.5" required value={form.price} onChange={(e) => setForm({...form, price: e.target.value})} className="rounded-xl mt-1" data-testid="pkg-price"/></div>
              <div><Label>Créditos base (B$)</Label><Input type="number" step="0.01" min="0" required value={form.credits} onChange={(e) => setForm({...form, credits: e.target.value})} className="rounded-xl mt-1" data-testid="pkg-credits"/></div>
              <div><Label>Bônus (B$ extra)</Label><Input type="number" step="0.01" min="0" value={form.bonus} onChange={(e) => setForm({...form, bonus: e.target.value})} className="rounded-xl mt-1" data-testid="pkg-bonus"/></div>
              <div><Label>Ordem (menor aparece primeiro)</Label><Input type="number" value={form.order} onChange={(e) => setForm({...form, order: e.target.value})} className="rounded-xl mt-1"/></div>
              <div className="sm:col-span-2"><Label>Descrição</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="rounded-xl mt-1"/></div>
              <div className="flex items-center gap-2"><Switch checked={!!form.active} onCheckedChange={(v) => setForm({...form, active: v})}/><Label>Ativo (visível na lojinha)</Label></div>
              <div className="flex items-center gap-2"><Switch checked={!!form.highlight} onCheckedChange={(v) => setForm({...form, highlight: v})}/><Label>Destaque</Label></div>
            </div>

            {price > 0 && totalCredits > 0 && (
              <div className={`p-3 rounded-xl border-2 ${warn ? 'border-red-300 bg-red-50' : 'border-emerald-300 bg-emerald-50'}`}>
                <div className="flex items-start gap-2">
                  {warn ? <AlertTriangle className="text-red-700 shrink-0 mt-0.5" size={18}/> : <CheckCircle className="text-emerald-700 shrink-0 mt-0.5" size={18}/>}
                  <div>
                    <h4 className={`font-display font-bold ${warn ? 'text-red-900' : 'text-emerald-900'}`}>
                      {warn ? "⚠️ Pacote em PREJUÍZO (todo cashback usado, 0% breakage)" : "✓ Margem positiva garantida"}
                    </h4>
                    <p className="text-xs mt-1 opacity-90">
                      Comerciante paga <strong>R$ {price.toFixed(2)}</strong> → recebe <strong>B$ {totalCredits.toFixed(2)}</strong>.
                      {form.kind === "cashback" ? (
                        <> Se TUDO for usado pelos clientes na rede, a plataforma paga 95% do total a outros comerciantes (R$ {(totalCredits*0.95).toFixed(2)}).</>
                      ) : (
                        <> Voucher é fechado (resgate na própria loja) — sem obrigação financeira da plataforma.</>
                      )}
                    </p>
                    <p className="text-xs mt-1.5 font-mono">
                      Margem estimada (0% breakage): <strong>R$ {margin0.toFixed(2)}</strong> · (30% breakage): <strong>R$ {margin30.toFixed(2)}</strong>
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={close} className="rounded-xl">Cancelar</Button>
              <Button type="submit" disabled={busy} className="bg-blue-900 hover:bg-blue-950 text-white rounded-xl" data-testid="pkg-save">
                {busy ? "Salvando..." : "Salvar"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
